/* Sukarah quick-shade: fast quick-buy sheet + shade switching.
   Extends Prestige's QuickBuyModal (does not replace it):
   - opens the sheet immediately with a skeleton, fetches content in background
   - session cache (5 min TTL) + interaction-triggered prefetch of shade products
   - optimistic tile/name updates, ATC disabled while switching, rollback on error
   - 220ms mobile slide (Prestige's own vendor motion library and easing) */
import { animate, timeline } from 'vendor';

const QuickBuyModal = window.customElements.get('quick-buy-modal');

if (QuickBuyModal) {
  const baseShow = Object.getPrototypeOf(QuickBuyModal.prototype).show;
  const CACHE_TTL = 5 * 60 * 1000;
  const htmlCache = new Map();

  const root = () => window.Shopify?.routes?.root || '/';

  const warmFirstImage = (html) => {
    const template = html.split('id="quick-buy-content"')[1] || '';
    const match = template.match(/<img[^>]+src="([^"]+)"/);

    if (match) {
      new Image().src = match[1].replace(/&amp;/g, '&');
    }
  };

  const fetchQuickBuyHtml = (handle) => {
    const cached = htmlCache.get(handle);

    if (cached && Date.now() - cached.time < CACHE_TTL) {
      return cached.promise;
    }

    const promise = fetch(`${root()}products/${handle}`).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    });

    promise.then(warmFirstImage).catch(() => htmlCache.delete(handle));
    htmlCache.set(handle, { time: Date.now(), promise });
    return promise;
  };

  const parseQuickBuyContent = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const template = doc.getElementById('quick-buy-content');

    if (!template) return null;

    template.content.querySelectorAll('noscript').forEach((el) => el.remove());
    return template.content;
  };

  /* Background prefetch of sibling shades, max 2 concurrent */
  const prefetchQueue = [];
  let prefetchActive = 0;

  const pumpPrefetch = () => {
    while (prefetchActive < 2 && prefetchQueue.length) {
      const handle = prefetchQueue.shift();
      prefetchActive += 1;
      fetchQuickBuyHtml(handle)
        .catch(() => {})
        .finally(() => {
          prefetchActive -= 1;
          pumpPrefetch();
        });
    }
  };

  const prefetchSiblings = (modal) => {
    const current = modal.getAttribute('handle');

    modal.querySelectorAll('[data-quick-buy-shade]').forEach((tile) => {
      const handle = tile.getAttribute('data-quick-buy-shade');

      if (handle && handle !== current && !prefetchQueue.includes(handle)) {
        prefetchQueue.push(handle);
      }
    });

    pumpPrefetch();
  };

  const SKELETON = `
    <div class="quick-buy-modal__content quick-buy-skeleton" aria-busy="true">
      <div class="quick-buy-skeleton__media"></div>
      <div class="quick-buy-skeleton__lines">
        <div class="quick-buy-skeleton__line" style="width: 55%"></div>
        <div class="quick-buy-skeleton__line" style="width: 30%"></div>
        <div class="quick-buy-skeleton__line" style="width: 75%"></div>
        <div class="quick-buy-skeleton__line quick-buy-skeleton__line--button"></div>
      </div>
    </div>`;

  QuickBuyModal.prototype.show = async function (animateIn = true) {
    const handle = this.getAttribute('handle');
    this._sukSeq = (this._sukSeq || 0) + 1;
    const seq = this._sukSeq;

    let opening = Promise.resolve();

    if (!this.open) {
      if (!this.firstElementChild) this.innerHTML = SKELETON;
      opening = baseShow.call(this, animateIn);
    }

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const html = await fetchQuickBuyHtml(handle);

      if (seq === this._sukSeq && this.getAttribute('handle') === handle) {
        const content = parseQuickBuyContent(html);

        if (content) {
          this.replaceChildren(content);
          this.classList.remove('quick-shade-switching');
          this._sukRevert = null;
          window.Shopify?.PaymentButton?.init();
          prefetchSiblings(this);
        }
      }
    } catch (error) {
      if (seq === this._sukSeq) {
        if (this.querySelector('.quick-buy-skeleton')) {
          /* Initial content failed: fall back to plain navigation */
          window.location.href = `${root()}products/${handle}`;
        } else if (this._sukRevert) {
          this._sukRevert();
          this._sukRevert = null;
        }
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }

    return opening;
  };

  /* Slightly faster open animation (Prestige defaults: 200ms fade desktop / 300ms slide mobile) */
  QuickBuyModal.prototype.createEnterAnimationControls = function () {
    if (window.matchMedia('(min-width: 700px)').matches) {
      return animate(this, { opacity: [0, 1] }, { duration: 0.2 });
    }

    const easing = [0.645, 0.045, 0.355, 1];

    return timeline([
      [this.getShadowPartByName('overlay'), { opacity: [0, 1] }, { duration: 0.22, easing }],
      [this.getShadowPartByName('content'), { transform: ['translateY(100%)', 'translateY(0)'] }, { duration: 0.22, easing, at: '<' }]
    ]);
  };

  const showSwitchError = (picker) => {
    if (!picker) return;

    const message = picker.getAttribute('data-shade-error-message') || 'Please try again.';
    let el = picker.querySelector('.shade-picker__error');

    if (!el) {
      el = document.createElement('p');
      el.className = 'shade-picker__error text-sm';
      picker.append(el);
    }

    el.textContent = message;
    setTimeout(() => el.remove(), 4000);
  };

  /* Optimistic shade switching inside an open sheet */
  document.addEventListener('click', (event) => {
    const tile = event.target.closest('[data-quick-buy-shade]');

    if (!tile) return;

    const modal = tile.closest('quick-buy-modal');

    if (!modal) return; /* outside the sheet (e.g. product page): navigate normally */

    event.preventDefault();

    if (tile.classList.contains('is-selected') || tile.getAttribute('aria-disabled') === 'true') return;

    const newHandle = tile.getAttribute('data-quick-buy-shade');
    const previousHandle = modal.getAttribute('handle');
    const picker = tile.closest('.shade-picker');
    const previousTile = picker?.querySelector('.shade-picker__tile.is-selected');
    const nameEl = picker?.querySelector('.shade-picker__header strong');
    const previousName = nameEl?.textContent;
    const buyButtons = modal.querySelectorAll('form button[type="submit"], .shopify-payment-button button');

    previousTile?.classList.remove('is-selected');
    tile.classList.add('is-selected');
    if (nameEl) nameEl.textContent = tile.getAttribute('title') || '';
    modal.classList.add('quick-shade-switching');
    buyButtons.forEach((button) => (button.disabled = true));

    modal._sukRevert = () => {
      tile.classList.remove('is-selected');
      previousTile?.classList.add('is-selected');
      if (nameEl && previousName != null) nameEl.textContent = previousName;
      modal.classList.remove('quick-shade-switching');
      buyButtons.forEach((button) => (button.disabled = false));
      modal.setAttribute('handle', previousHandle);
      showSwitchError(picker);
    };

    modal.setAttribute('handle', newHandle);
    modal.show();
  });

  /* Interaction-triggered prefetch on the card bars */
  const prefetchFromBar = (event) => {
    const bar = event.target.closest?.('.product-card__quick-add-bar[aria-controls]');

    if (!bar) return;

    const modal = document.getElementById(bar.getAttribute('aria-controls'));
    const handle = modal?.getAttribute('handle');

    if (handle) fetchQuickBuyHtml(handle).catch(() => {});
  };

  document.addEventListener('pointerenter', prefetchFromBar, true);
  document.addEventListener('focusin', prefetchFromBar, true);
  document.addEventListener('touchstart', prefetchFromBar, { passive: true, capture: true });
}
